// Column widths the Opportunities table is laid out with, and the table's minimum width derived from them.
// opportunityColumnsView sizes the columns from these and OpportunitiesPage floors the table with
// tableMinWidthPx, so the two cannot drift apart.

import type {OpportunityColumnName} from './opportunityColumns';
import {CUSTOM_FIELD_COLUMN_WIDTH_PX} from '../shared/components/custom-field-columns/customFieldColumnModel';

// The native columns' pixel budget while any custom column shows; with none, the table's own floor.
export const NATIVE_TABLE_MIN_WIDTH_PX = 1700;

// Each native column's share of the table, in percent. These MUST sum to 100 - see the note on the width
// budget in opportunityColumnsView.
export const NATIVE_COLUMN_WIDTH_PERCENT: Record<OpportunityColumnName, number> = {
	name: 11,
	client: 9,
	primaryContact: 10,
	targetValue: 7,
	winProbability: 8,
	stage: 9,
	startDate: 9,
	endDate: 8,
	quoteStatus: 8,
	quoteExpiry: 6,
	status: 8,
	salesperson: 7,
};

/** A native column's pixel width at the table's floor - its share of NATIVE_TABLE_MIN_WIDTH_PX. */
export const nativeColumnWidthPx = (name: OpportunityColumnName): number =>
	(NATIVE_COLUMN_WIDTH_PERCENT[name] * NATIVE_TABLE_MIN_WIDTH_PX) / 100;
// Each custom field column's fixed width.
export {CUSTOM_FIELD_COLUMN_WIDTH_PX};
// The row actions (kebab) column's fixed width.
// Not ours to choose: the design-system ActionColumn fixes its own width at 78px and takes no width prop.
// This mirrors it so the table's floor matches what is drawn; were it lower, the difference would come out
// of the Opportunity Name column, the one column without a fixed width.
export const ACTIONS_COLUMN_WIDTH_PX = 78;

/**
 * The table's minimum width. With no custom column the native columns are percentages and the floor is
 * NATIVE_TABLE_MIN_WIDTH_PX. Once any custom column shows, the floor is the sum of the columns actually
 * shown - each visible native column's pixel width, the actions column and each custom column. Counting a
 * hidden native column would leave slack that the fixed layout spreads over every column, custom ones
 * included, so they would come out wider than their fixed width.
 */
export function tableMinWidthPx(customColumnCount: number, visibleNativeColumns: Iterable<OpportunityColumnName>): number {
	if (customColumnCount === 0) return NATIVE_TABLE_MIN_WIDTH_PX;
	let native = 0;
	for (const name of visibleNativeColumns) native += nativeColumnWidthPx(name);
	return native + ACTIONS_COLUMN_WIDTH_PX + customColumnCount * CUSTOM_FIELD_COLUMN_WIDTH_PX;
}
